import {
  InternalNode,
  useNodesInitialized,
  useReactFlow,
  useStoreApi,
  Viewport,
} from '@xyflow/react';
import { useEffect, useRef } from 'react';

import { useFamilyTreeContext } from '../contexts';

/**
 * Returns true when the node is fully inside the visible pane, false when it is
 * clipping an edge or sitting entirely outside it.
 */
function isNodeWithinViewport(
  node: InternalNode,
  viewport: Viewport,
  containerWidth: number,
  containerHeight: number
): boolean {
  const { x, y, zoom } = viewport;
  const { x: nodeX, y: nodeY } = node.internals.positionAbsolute;
  const nodeWidth = node.measured.width ?? 0;
  const nodeHeight = node.measured.height ?? 0;

  // Project the node's corners from flow space into screen (pane) space.
  const left = nodeX * zoom + x;
  const top = nodeY * zoom + y;
  const right = (nodeX + nodeWidth) * zoom + x;
  const bottom = (nodeY + nodeHeight) * zoom + y;

  return (
    left >= 0 &&
    top >= 0 &&
    right <= containerWidth &&
    bottom <= containerHeight
  );
}

/**
 * Re-runs "fit view" after a new node is added to the family tree (e.g. when a
 * person is added via the relationships sidebar) — but only when that node
 * would otherwise be clipped or off-screen, so an addition that already lands
 * in view doesn't disrupt the user's current pan/zoom.
 *
 * The layout pipeline already queues a synchronous `fitViewEffect`, but that
 * fires before React Flow has measured the brand-new node. React Flow computes
 * fit bounds (and node positions) from measured dimensions, so the unmeasured
 * node is excluded and can land outside the viewport.
 *
 * `useNodesInitialized` flips to `false` when an unmeasured node is added and
 * back to `true` once it has been measured. We act on that transition — after
 * the initial mount fit (which the base `<ReactFlow fitView />` prop handles).
 * Depending only on `nodesInitialized` means drags, edit-mode toggles, and
 * viz-config changes (none of which add unmeasured nodes) never retrigger it.
 */
export function useFitViewOnNodesAdded() {
  const { getState } = useFamilyTreeContext();
  const { fitView, getInternalNode, getViewport } = useReactFlow();
  const store = useStoreApi();
  const nodesInitialized = useNodesInitialized();
  const knownNodeIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!nodesInitialized) return;

    // Filter out synthetic nodes so they neither trigger a refit nor affect the
    // fit bounds (mirrors `getFitViewOptions` in FamilyTreeFlowChart).
    const fittableNodes = getState().nodes.filter((node) => {
      if (node.type === 'person') {
        return !node.data.person.isSynthetic;
      }
      return true;
    });
    const currentNodeIds = new Set(fittableNodes.map((node) => node.id));

    // Skip the first measurement pass after mount; the default `fitView` prop
    // already framed the graph. Record the baseline node set.
    if (knownNodeIdsRef.current === null) {
      knownNodeIdsRef.current = currentNodeIds;
      return;
    }

    const baselineNodeIds = knownNodeIdsRef.current;
    const addedNodeIds = fittableNodes
      .map((node) => node.id)
      .filter((id) => !baselineNodeIds.has(id));
    knownNodeIdsRef.current = currentNodeIds;

    if (addedNodeIds.length === 0) return;

    // Only refit when a newly added node is clipping or fully outside the
    // current viewport. If the pane has no measured size, err toward refitting
    // so the new node can't be lost.
    const { width, height } = store.getState();
    const viewport = getViewport();
    const shouldRefit =
      !width ||
      !height ||
      addedNodeIds.some((id) => {
        const node = getInternalNode(id);
        // If the node can't be resolved, err toward refitting.
        if (!node) return true;
        return !isNodeWithinViewport(node, viewport, width, height);
      });

    if (shouldRefit) {
      void fitView({ nodes: fittableNodes });
    }
  }, [
    nodesInitialized,
    fitView,
    getInternalNode,
    getViewport,
    store,
    getState,
  ]);
}
